if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        // Only lift a location restriction; never re-enable a button Dawn disabled because the
        // variant is sold out or unavailable (marked with the not-available class).
        if (this.checkLocation() && !this.submitButton.classList.contains('not-available')) {
          this.submitButton.removeAttribute('disabled');
        }
        // On the product page itself, say up front when the cart is tied to the other store, rather
        // than only after Add to cart is pressed. Cards in a grid keep the message to the click.
        if (this.closest('product-info') && !this.closest('quick-add-modal')) this.refreshLocationNotice();
      }

      // The stores this form's item can be collected from ("Euclid", "Urbandale").
      get formStores() {
        return window.cartStores ? window.cartStores.parse(this.submitButton.getAttribute('data-locations')) : [];
      }

      // True when the item can join the cart: the cart is empty or unrestricted, the item has no store
      // information (a gift card, say), or the item is available at a store every cart line shares.
      checkLocation() {
        const current = window.cartStores ? window.cartStores.current() : [];
        const stores = this.formStores;
        if (!current.length) {
          this.locations = stores.join(',');
          return true;
        }
        if (!stores.length) {
          this.locations = current.join(',');
          return true;
        }
        const allowed = current.filter((store) => stores.includes(store));
        this.locations = (allowed.length ? allowed : current).join(',');
        return allowed.length > 0;
      }

      escapeText(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      locationMessage() {
        const cartStore = this.escapeText((window.cartStores ? window.cartStores.current() : []).join(' and '));
        const here = this.escapeText(this.formStores.join(' or '));
        return (
          `<div class="location-error"><span class="location-header">Your cart is for pickup at ${cartStore}</span>` +
          `<p>Each online order is picked up at one store, and this item is at ${here}. ` +
          `Finish your ${cartStore} order first, or empty your cart to shop ${here}.</p>` +
          `<p class="location-error__actions"><a href="${window.routes.cart_url}">View cart</a>` +
          `<button type="button" class="link location-error__clear">Empty cart</button></p></div>`
        );
      }

      showLocationMessage() {
        this.handleErrorMessage(this.locationMessage());
        const clear = this.querySelector('.location-error__clear');
        if (!clear) return;
        clear.addEventListener('click', () => {
          clear.disabled = true;
          fetch(`${window.routes.cart_clear_url || '/cart/clear'}.js`, { method: 'POST', headers: { Accept: 'application/json' } })
            .then(() => window.location.reload())
            .catch(() => {
              clear.disabled = false;
            });
        });
      }

      refreshLocationNotice() {
        if (this.checkLocation()) {
          if (this.querySelector('.location-error')) this.handleErrorMessage();
        } else {
          this.showLocationMessage();
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // Check against the cart as it is now, not as it was when this page was drawn: a page
        // restored with Back, or a second tab, can predate an add from the other store
        // (snippets/cart-stores.liquid). refresh() never rejects; offline it keeps the page's list.
        this.submitButton.setAttribute('aria-disabled', true);
        const freshCart = window.cartStores && window.cartStores.refresh ? window.cartStores.refresh() : Promise.resolve();
        freshCart.then(() => {
          this.submitButton.removeAttribute('aria-disabled');
          // The store restriction (data-locations on the submit button) and the "Located at"
          // line-item property both come from the vendor rule in the Liquid form, so they
          // always agree with each other and with the "Available at" label on the cards.
          if (!this.checkLocation()) {
            this.showLocationMessage();
            return;
          }
          this.addToCart(evt);
        });
      }

      addToCart(evt) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            // The item is in the cart now, so its store becomes part of the one-store rule.
            if (window.cartStores) window.cartStores.add(this.submitButton.getAttribute('data-locations'));

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.innerHTML = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // Keep the not-available marker in sync so the location code (checkLocation here and
        // updateLocation in cart.js) can tell a sold-out button from a location-restricted one.
        this.submitButton.classList.toggle('not-available', disable);
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
